'use client';

import ReactMarkdown from 'react-markdown';

const markdown = `
# Consumers

This site collates performance data on DC workplace pensions. Definitions of key terms for members and savers are explained on this page.

## What is a pension?

A pension is a tax-efficient savings vehicle designed to provide an income in retirement. There are specific rules on how much can be saved, and when and how these funds can be accessed.

Employees can save through a workplace pension, where payments are taken directly from their salary and their employer is also likely to contribute.

They can also choose to save into a private pension. People can contribute to both workplace and private pensions at the same time.

## Types of pension

Workplace pensions will be either defined contribution (DC) or defined benefit (DB). Most private sector companies now only offer DC pension benefits.

- **Defined contribution (DC):** A retirement savings plan where both the individual and, in many cases, their employer contribute to a pension pot that is invested in the stock market over time. The final size of their pot at retirement will depend on the total contributions made, investment performance, and charges levied on the fund.
- **Defined benefit (DB):** These pension plans pay a guaranteed income in retirement, linked to the employee’s salary and length of time they have been a member of the scheme. The payments will be made for life. These are also known as ‘final salary’ or ‘career average’ schemes. In most cases, employees will still contribute to the scheme, but it is the employer that takes the risk if the scheme does not have enough assets or sufficient investment growth to meet these pension promises. These are now only widely available in the public sector.

All private pensions are defined contribution plans.

## Auto-enrolment (AE)

All employers are required by law to set up a pension plan for their staff and pay a minimum amount into it. All employees aged between 22 and State Pension Age and earning over £10,000 a year will be automatically enrolled into these schemes, with further contributions made from their salary unless they specifically opt out.

## Minimum contribution levels

- Employers pay 3% of employees’ qualifying earnings  
- Employees pay 5% of qualifying earnings  
- This means approximately 8% of employees’ earnings are invested into the pension each year  
- Qualifying earnings refer to earnings between £6,240 and £50,270 per year  
- Employers and employees can opt to pay more than the minimum levels into these schemes, subject to wider annual allowances on pension contributions  
- Younger workers, those earning less than £10,000, and those working beyond State Pension Age have the right to join an employer’s AE scheme but won’t be automatically enrolled  

## Pension contribution limits (for the 2024/25 tax year)

For most people, the annual allowance is £60,000, or the value of their annual salary — whichever is lower. These contributions qualify for tax relief.

Contributions above this threshold may be subject to a tax charge, effectively removing the tax relief. The annual allowance includes the total value of both employee and employer contributions.

Some people may not qualify for the full annual allowance:

- **Higher earners:** The annual allowance is tapered for those earning over £200,000, reducing what they can save into a pension each year. At £260,000, the maximum that can be saved is £10,000 a year.
- Those who have already accessed their pension flexibly will also have their annual allowance reduced to £10,000 under the Money Purchase Annual Allowance (MPAA).
- **Non-taxpayers** can contribute up to £2,880 into a pension and will receive tax relief, bringing the total up to £3,600.

## Tax relief

Pension contributions (up to the relevant annual allowance) qualify for tax relief. The government adds basic-rate tax relief of 20% to all qualifying contributions, meaning that for every £80 contributed, £100 will go into the pension plan.

Higher and additional-rate taxpayers can claim further tax relief through their tax return, reducing their overall tax bill. This isn’t paid directly into their pension.

## Default investments

The default fund is the fund or range of funds employees will automatically be invested into. Around 90% of DC workplace savers are in the default option, which typically invests in a mix of growth assets.

Many workplace pensions operate ‘lifestyling’ glidepaths, which aim to reduce investment risk as people approach retirement age.

The CAPA website compares the performance of the default funds for the largest multi-employer DC workplace pensions in the UK.

## Pension charges

All auto-enrolment pension schemes are subject to a maximum charge of 0.75% of the pension pot value. In reality, most workplace pension schemes have charges well below this level.

This charge cap applies only to the default investment fund used in workplace pensions. Those who select a different fund or investment option may pay higher charges.

The CAPA website shows default fund performance before charges are applied.

## Accessing pension benefits

Currently, people can access their pension from the age of 55, although this will increase to 57 from April 2028. Those accessing pension funds earlier may face hefty tax charges, except in cases of serious medical conditions affecting life expectancy.

When accessing pension benefits, savers can typically take up to 25% as a tax-free cash sum, with the remaining funds subject to income tax as they are withdrawn.

There are three main options when it comes to taking pension benefits:

- **Annuity:** Provides a guaranteed income for life, bought with pension savings. The exact amount paid depends on age, health, and annuity rates. It is possible to buy an annuity with part of a pension fund.
- **Drawdown (Flexi-Access Drawdown):** The pension remains invested, and the member withdraws money as needed. The tax-free cash can be taken upfront, with the remainder taxed as income when drawn down. Money stays invested, so it can continue to grow, but there is a risk that savers may outlive their funds, especially if investment returns are poor.
- **Flexible payments (Uncrystallised Funds Pension Lump Sum - UFPLS):** Members withdraw lump sums directly from their pension without moving into drawdown. Each withdrawal is 25% tax-free, with the remaining 75% taxed as income. This is suitable for those wanting ad hoc payments rather than a regular income.

Accessing just the tax-free cash does not affect your annual allowance for future contributions. Moving into drawdown or taking flexible payments beyond this tax-free cash limit will mean future contributions are subject to the MPAA.


`;

export default function ConsumersPage(): JSX.Element {
	return (
		<div className='p-6'>
			<ReactMarkdown
				components={{
					h1: ({ children }) => <h1 className='text-3xl font-bold mb-6'>{children}</h1>,
					h2: ({ children }) => <h2 className='text-2xl font-semibold mt-6 mb-4'>{children}</h2>,
					h3: ({ children }) => <h3 className='text-xl font-semibold mt-6 mb-3'>{children}</h3>,
					p: ({ children }) => <p className='mb-4 leading-relaxed'>{children}</p>,
					strong: ({ children }) => <strong className='font-semibold'>{children}</strong>,
					a: ({ children, href }) => (
						<a href={href} className='text-blue-600 underline hover:text-blue-800'>
							{children}
						</a>
					),
					ul: ({ children }) => <ul className='list-disc list-inside mb-4'>{children}</ul>,
					li: ({ children }) => <li className='mb-2'>{children}</li>,
				}}
			>
				{markdown}
			</ReactMarkdown>
		</div>
	);
}
